﻿import { toast } from 'sonner';
import i18next from 'i18next';
import { Logger } from '@/lib/app-log.js';
import { useRiskRegisterStore } from '../../store/risk-register.store.js';
import { useRiskExistingControlsStore } from '../../store/risk-existing-controls.store.js';
import { useRiskRelatedTreatmentsStore } from '../../store/risk-related-treatments.store.js';
import { useRiskRelatedCausesStore } from '../../store/risk-related-causes.store.js';
import { useRiskRelatedConsequencesStore } from '../../store/risk-related-consequences.store.js';
import { useRiskRelatedAssetsStore } from '../../store/risk-related-assets.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';
import { downloadBlob } from '@/lib/download-blob.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

/**
 * RiskRegister Integration Layer
 * Connects SDK Service to Zustand Store
 *
 * Association mutations do not bump reference versions locally: the API broadcasts
 * `riskRegister` (+ counterpart) invalidation and the detail bootstrap refetches the hub.
 */
export class RiskRegisterIntegration {
    constructor(sdkService, controlService) {
        this.service = sdkService;
        this.controlService = controlService;
    }

    /**
     * Fetch all items and update store
     */
    async fetchRiskRegisters(params = {}) {
        const store = useRiskRegisterStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const currentPagination = store.pagination;
            const queryParams = {
                page: params.page || currentPagination.currentPage,
                pageSize: params.pageSize || currentPagination.itemsPerPage,
                search: params.search !== undefined ? params.search : store.searchQuery,
                sortBy: params.sortBy || store.sorting.sortBy,
                sortOrder: params.sortDirection || store.sorting.sortDirection,
                ...params,
                ...mapColumnFiltersForQuery(params.columnFilters),
                tenantId
            };
            delete queryParams.columnFilters;

            const response = await this.service.getAll(queryParams);

            store.setRiskRegisters(response.data || []);

            // Handle Pagination Mapping & Calculation
            const paginationData = response.meta || {};
            const currentPage = parseInt(paginationData.page || params.page || 1);
            const itemsPerPage = parseInt(paginationData.pageSize || params.pageSize || 10);
            const totalItems = parseInt(paginationData.total || 0);
            const totalPages = parseInt(paginationData.totalPages || Math.ceil(totalItems / itemsPerPage) || 0);

            store.setPagination({
                currentPage,
                itemsPerPage,
                totalItems,
                totalPages,
                hasNextPage: currentPage < totalPages,
                hasPreviousPage: currentPage > 1
            });

            // Handle Unique Values
            if (response.uniqueValues) {
                store.setUniqueValues(response.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async createFilteredReportViewerSession(payload) {
        try {
            const response = await this.service.createFilteredReportViewerSession(payload);
            return { success: true, data: response?.data ?? response };
        } catch (error) {
            const message = i18next.t('riskRegister:reports.viewerError', {
                defaultValue: 'Risk report could not be opened. Please try again.',
            });
            Logger.error('RiskRegisterIntegration.createFilteredReportViewerSession failed', { error: error?.message });
            toast.error(message);
            return { success: false, error: message };
        }
    }


    async exportFilteredReport(payload) {
        try {
            const blob = await this.service.exportFilteredReport(payload);
            const fileName = i18next.t('riskRegister:reports.fileName', { lng: payload.locale });
            downloadBlob(blob, fileName);
            toast.success(i18next.t('riskRegister:reports.exportSuccess', {
                defaultValue: 'Risk report created.',
            }));
            return { success: true };
        } catch (error) {
            const message = i18next.t('riskRegister:reports.exportError', {
                defaultValue: 'Risk report could not be created. Please try again.',
            });
            Logger.error('RiskRegisterIntegration.exportFilteredReport failed', { error: error?.message });
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Get by ID
     */
    async fetchRiskRegisterById(id) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        if (store.setCurrentRisk) store.setCurrentRisk(null);

        try {
            const response = await this.service.getById(id);
            store.setCurrentRisk(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            if (store.setCurrentRisk) store.setCurrentRisk(null);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Fetch Stats (Custom operation)
     */
    /**
     * Create item
     */
    async createRiskRegister(data) {
        const store = useRiskRegisterStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const response = await this.service.create({ ...data, tenantId });

            toast.success(i18next.t('riskRegister:messages.createSuccess', { defaultValue: 'Risk created successfully' }));

            store.closeForm();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update item
     */
    async updateRiskRegister(id, data) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('riskRegister:messages.updateSuccess', { defaultValue: 'Risk updated successfully' }));

            store.closeForm();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete item
     */
    async deleteRiskRegister(id, opts = {}) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);

        try {
            await this.service.delete(id);
            if (!opts.silent) {
                toast.success(i18next.t('integrationToasts:riskDeletedSuccessfully', { defaultValue: 'Risk deleted successfully' }));
            }
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (!opts.silent) toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk Delete
     */
    async bulkDeleteRiskRegisters(ids) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);

        try {
            await this.service.bulkDelete(ids);
            toast.success(i18next.t('integrationToasts:selectedRisksDeletedSuccessfully', { defaultValue: 'Selected risks deleted successfully' }));
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Fetch linked Controls
     */
    async fetchControls(id, params = {}) {
        const store = useRiskExistingControlsStore.getState();
        store.setLoading(true);
        store.clearError?.();

        try {
            const currentPagination = store.pagination || { currentPage: 1, itemsPerPage: 10 };
            const currentParams = {
                page: currentPagination.currentPage,
                limit: currentPagination.itemsPerPage,
                search: store.searchQuery || '',
                sort: store.sort ? JSON.stringify(store.sort) : undefined,
                ...store.columnFilters,
                ...params,
            };

            const response = await this.service.getControls(id, currentParams);

            const mappedData = (response.data || []).map(item => ({
                ...item,
                name: item.name || item.control
            }));

            store.setData?.(mappedData);

            const paginationData = response.meta || {};
            const currentPage = parseInt(paginationData.page || currentParams.page || 1);
            const itemsPerPage = parseInt(paginationData.limit || currentParams.limit || 10);
            const totalItems = parseInt(paginationData.total || 0);
            const totalPages = parseInt(paginationData.pages || Math.ceil(totalItems / itemsPerPage) || 0);

            store.setPagination?.({
                currentPage,
                itemsPerPage,
                totalItems,
                totalPages,
            });

            if (paginationData.uniqueOwners && store.setUniqueOwners) {
                store.setUniqueOwners(paginationData.uniqueOwners);
            }
            if (paginationData.uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(paginationData.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError?.(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Fetch linked Treatments (Paginated)
     */
    async fetchTreatments(id, params = {}) {
        const store = useRiskRelatedTreatmentsStore.getState();
        store.setLoading(true);
        store.clearError?.();

        try {
            const currentPagination = store.pagination || { currentPage: 1, itemsPerPage: 10 };
            const currentParams = {
                page: currentPagination.currentPage,
                limit: currentPagination.itemsPerPage,
                search: store.searchQuery || '',
                sort: store.sort ? JSON.stringify(store.sort) : undefined,
                ...store.columnFilters,
                ...params,
            };

            const response = await this.service.getRelatedTreatments(id, currentParams);

            store.setData?.(response.data || []);

            const paginationData = response.meta || {};
            const currentPage = parseInt(paginationData.page || currentParams.page || 1);
            const itemsPerPage = parseInt(paginationData.limit || currentParams.limit || 10);
            const totalItems = parseInt(paginationData.total || 0);
            const totalPages = parseInt(paginationData.pages || Math.ceil(totalItems / itemsPerPage) || 0);

            store.setPagination?.({
                currentPage,
                itemsPerPage,
                totalItems,
                totalPages,
            });

            if (paginationData.uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(paginationData.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError?.(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Link Controls
     */
    async addControls(id, controlIds) {
        const store = useRiskExistingControlsStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.addControls(id, controlIds);

            toast.success(i18next.t('integrationToasts:controlsAddedSuccessfully', { defaultValue: 'Controls added successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Unlink Control
     */
    async removeControl(id, controlId, opts = {}) {
        const store = useRiskExistingControlsStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.removeControl(id, controlId);
            if (!opts.silent) {
                toast.success(i18next.t('integrationToasts:controlRemovedSuccessfully', { defaultValue: 'Control removed successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (!opts.silent) toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk unlink controls from a risk. Single summary toast.
     */
    async removeControlsBulk(id, controlIds) {
        const store = useRiskExistingControlsStore.getState();
        store.setLoading(true);
        let count = 0;
        try {
            const cleanIds = Array.isArray(controlIds) ? controlIds.filter(Boolean) : [];
            for (const cid of cleanIds) {
                await this.service.removeControl(id, cid);
                count++;
            }
            toast.success(i18next.t('riskRegister:messages.unlinkSuccess', { count, defaultValue: 'Links removed successfully.' }));
            return { success: true, count };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, count };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Link Treatments to Risk
     */
    async linkTreatmentsToRisk(id, treatmentIds) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.addTreatments(id, treatmentIds);


            toast.success(i18next.t('integrationToasts:actionsAddedSuccessfully', { defaultValue: 'Actions added successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Unlink Treatment from Risk
     */
    async unlinkTreatmentFromRisk(id, treatmentId, opts = {}) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.removeTreatment(id, treatmentId);


            if (!opts.silent) {
                toast.success(i18next.t('integrationToasts:actionLinkRemovedSuccessfully', { defaultValue: 'Action link removed successfully' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (!opts.silent) toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk unlink treatments from a risk. Single summary toast.
     */
    async unlinkTreatmentsBulkFromRisk(id, treatmentIds) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        let count = 0;
        try {
            const cleanIds = Array.isArray(treatmentIds) ? treatmentIds.filter(Boolean) : [];
            for (const tid of cleanIds) {
                await this.service.removeTreatment(id, tid);
                count++;
            }
            toast.success(i18next.t('riskTreatment:messages.bulkUnlinkSuccess', {
                count,
                defaultValue: 'Successfully unlinked {{count}} selected activities.',
            }));
            return { success: true, count };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, count };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchRelatedCauses(id, params = {}) {
        const store = useRiskRelatedCausesStore.getState();
        store.setLoading(true);
        store.clearError?.();

        try {
            const currentPagination = store.pagination || { currentPage: 1, itemsPerPage: 10 };
            const currentParams = {
                page: currentPagination.currentPage,
                limit: currentPagination.itemsPerPage,
                search: store.searchQuery || '',
                sort: store.sort ? JSON.stringify(store.sort) : undefined,
                ...store.columnFilters,
                ...params,
            };

            const response = await this.service.getRelatedCauses(id, currentParams);

            store.setData?.(response.data || []);

            const paginationData = response.meta || {};
            const currentPage = parseInt(paginationData.page || currentParams.page || 1);
            const itemsPerPage = parseInt(paginationData.limit || currentParams.limit || 10);
            const totalItems = parseInt(paginationData.total || 0);
            const totalPages = parseInt(paginationData.pages || Math.ceil(totalItems / itemsPerPage) || 0);

            store.setPagination?.({
                currentPage,
                itemsPerPage,
                totalItems,
                totalPages,
            });

            if (paginationData.uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(paginationData.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError?.(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async linkRootCausesToRisk(id, rootCauseIds) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.addCauses(id, rootCauseIds);
            toast.success(i18next.t('integrationToasts:riskCausesLinked', { defaultValue: 'Risk causes linked' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async unlinkRootCauseFromRisk(id, rootCauseId) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.removeCause(id, rootCauseId);
            toast.success(i18next.t('integrationToasts:riskCauseLinkRemoved', { defaultValue: 'Risk cause link removed' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchRelatedConsequences(id, params = {}) {
        const store = useRiskRelatedConsequencesStore.getState();
        store.setLoading(true);
        store.clearError?.();

        try {
            const currentPagination = store.pagination || { currentPage: 1, itemsPerPage: 10 };
            const currentParams = {
                page: currentPagination.currentPage,
                limit: currentPagination.itemsPerPage,
                search: store.searchQuery || '',
                sort: store.sort ? JSON.stringify(store.sort) : undefined,
                ...store.columnFilters,
                ...params,
            };

            const response = await this.service.getRelatedConsequences(id, currentParams);

            store.setData?.(response.data || []);

            const paginationData = response.meta || {};
            const currentPage = parseInt(paginationData.page || currentParams.page || 1);
            const itemsPerPage = parseInt(paginationData.limit || currentParams.limit || 10);
            const totalItems = parseInt(paginationData.total || 0);
            const totalPages = parseInt(paginationData.pages || Math.ceil(totalItems / itemsPerPage) || 0);

            store.setPagination?.({
                currentPage,
                itemsPerPage,
                totalItems,
                totalPages,
            });

            if (paginationData.uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(paginationData.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError?.(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async linkConsequencesToRisk(id, consequenceIds) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.addConsequences(id, consequenceIds);
            toast.success(i18next.t('integrationToasts:riskConsequencesLinked', { defaultValue: 'Risk consequences linked' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async unlinkConsequenceFromRisk(id, consequenceId) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.removeConsequence(id, consequenceId);
            toast.success(i18next.t('integrationToasts:riskConsequenceLinkRemoved', { defaultValue: 'Risk consequence link removed' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchRelatedAssets(id, params = {}) {
        const store = useRiskRelatedAssetsStore.getState();
        store.setLoading(true);
        store.clearError?.();

        try {
            const currentPagination = store.pagination || { currentPage: 1, itemsPerPage: 10 };
            const currentParams = {
                page: currentPagination.currentPage,
                limit: currentPagination.itemsPerPage,
                search: store.searchQuery || '',
                sort: store.sort ? JSON.stringify(store.sort) : undefined,
                ...store.columnFilters,
                ...params,
            };

            const response = await this.service.getRelatedAssets(id, currentParams);

            store.setData?.(response.data || []);

            const paginationData = response.meta || {};
            const currentPage = parseInt(paginationData.page || currentParams.page || 1);
            const itemsPerPage = parseInt(paginationData.limit || currentParams.limit || 10);
            const totalItems = parseInt(paginationData.total || 0);
            const totalPages = parseInt(paginationData.pages || Math.ceil(totalItems / itemsPerPage) || 0);

            store.setPagination?.({
                currentPage,
                itemsPerPage,
                totalItems,
                totalPages,
            });

            if (paginationData.uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(paginationData.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError?.(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async linkAssetsToRisk(id, assetIds) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.linkAssets(id, assetIds);
            toast.success(i18next.t('integrationToasts:assetsLinkedSuccessfully', { defaultValue: 'Assets linked successfully' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async unlinkAssetFromRisk(id, assetId) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.unlinkAsset(id, assetId);
            toast.success(i18next.t('integrationToasts:assetLinkRemoved', { defaultValue: 'Asset link removed' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async unlinkAssetsBulkFromRisk(id, assetIds) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.unlinkAssetsBulk(id, assetIds);
            toast.success(i18next.t('assets:messages.multiUnlinked', {
                count: assetIds.length,
                defaultValue: '{{count}} links removed.',
            }));
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async unlinkAssetSourceFromRisk(id, assetId, source, refId) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.unlinkAssetSource(id, assetId, { source, refId });
            await this.fetchRiskRegisterById(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async linkAssetsByCategory(id, assetCategoryId) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.linkAssetsByCategory(id, assetCategoryId);
            await this.fetchRiskRegisterById(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async unlinkAssetsByCategory(id, assetCategoryId) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.unlinkAssetsByCategory(id, assetCategoryId);
            await this.fetchRiskRegisterById(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async linkAssetsByProcess(id, processId) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.linkAssetsByProcess(id, processId);
            await this.fetchRiskRegisterById(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async unlinkAssetsByProcess(id, processId) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.unlinkAssetsByProcess(id, processId);
            await this.fetchRiskRegisterById(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchRelatedBias(id, params = {}) {
        try {
            const response = await this.service.getRelatedBias(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    async addBiasToRisk(id, biaIds, opts = {}) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.addBias(id, biaIds);
            if (!opts.silent) {
                toast.success(
                    i18next.t('riskRegister:messages.biasLinkedSuccess', {
                        defaultValue: 'BIAs linked successfully',
                    }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async removeBiaFromRisk(id, biaId, opts = {}) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.removeBia(id, biaId);
            if (!opts.silent) {
                toast.success(
                    i18next.t('riskRegister:messages.biaUnlinkedSuccess', {
                        defaultValue: 'BIA unlinked successfully',
                    }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchRelatedObjectives(id, params = {}) {
        try {
            const response = await this.service.getRelatedObjectives(id, params);
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, data: [], pagination: {}, error: message };
        }
    }

    async addObjectivesToRisk(id, objectiveIds, opts = {}) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.addObjectives(id, objectiveIds);
            if (!opts.silent) {
                toast.success(
                    i18next.t('riskRegister:messages.objectivesLinkedSuccess', {
                        defaultValue: 'Objectives linked successfully',
                    }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async removeObjectiveFromRisk(id, objectiveId, opts = {}) {
        const store = useRiskRegisterStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.removeObjective(id, objectiveId);
            if (!opts.silent) {
                toast.success(
                    i18next.t('riskRegister:messages.objectiveUnlinkedSuccess', {
                        defaultValue: 'Objective unlinked successfully',
                    }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchRiskPartyTemplate(params = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: null };
        }
        try {
            const response = await this.service.getPartyTemplate(params);
            return {
                success: response.success !== false,
                data: response.data,
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, data: null, error: message };
        }
    }

    async fetchRiskParty(riskId) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !riskId) {
            return { success: false, data: null, partyTemplate: null };
        }
        try {
            const response = await this.service.getParty(riskId);
            return {
                success: response.success !== false,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, data: null, partyTemplate: null, error: message };
        }
    }

    async syncRiskParty(riskId, payload, options = {}) {
        const { silentSuccess = false } = options;
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !riskId) {
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }
        try {
            const response = await this.service.syncParty(riskId, payload);
            if (!silentSuccess) {
                toast.success(
                    i18next.t('riskRegister:ownership.saveSuccess', {
                            defaultValue: 'Ownership and roles saved',
                        }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }

    notifyLinkNoSelection() {
        toast.error(
            i18next.t('riskRegister:messages.linkNoSelection', {
                defaultValue: 'Please select at least one control.',
            }),
        );
    }
}
